/**
 * App Providers
 *
 * Configure React Query and other global providers
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Suspense, useEffect } from 'react';
import { initMocks } from '@/shopify/mocks/browser';

// Lazy load MSW worker to avoid blocking initial render
// const MSWWorker = lazy(() => import('@/shopify/mocks/browser').then(m => ({ default: m.worker })));

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time in milliseconds that data remains fresh
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Time in milliseconds that inactive queries will remain in cache
      gcTime: 30 * 60 * 1000, // 30 minutes (was cacheTime)
      // Number of times to retry failed requests
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Delay between retries (exponential backoff)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus (only in development)
      refetchOnWindowFocus: import.meta.env.DEV,
      // Refetch on reconnect
      refetchOnReconnect: true,
      // Refetch on mount if data is stale
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations
      retry: false,
      // Don't throw errors - handle them in mutation callbacks
      throwOnError: false,
    },
  },
});

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Initialize MSW mocks on mount (only in browser)
    if (typeof window !== 'undefined') {
      initMocks().catch((error) => {
        console.error('[MSW] Failed to initialize:', error);
      });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<LoadingFallback />}>
        {children}
      </Suspense>

      {/* React Query DevTools (only in development) */}
      {import.meta.env.DEV && <ReactQueryDevtools position="bottom" initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

// Export query client for direct access when needed
export { queryClient };
